import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Camera, Fuel, X } from 'lucide-react'
import { Card, CardContent } from '../../components/ui/Card'
import Button from '../../components/ui/Button'
import Input from '../../components/ui/Input'
import Alert from '../../components/ui/Alert'
import Loading from '../../components/ui/Loading'
import { useActiveContext } from '../../hooks/useActiveContext'
import { machineryExpensesService } from '../../services/machineryExpenses.service'
import { formatGallons } from '../../utils/formatQuantity'

const RegisterDumperFuel: React.FC = () => {
  const navigate = useNavigate()
  const { context, loading: ctxLoading } = useActiveContext()

  const fileInputRef = useRef<HTMLInputElement>(null)
  const [photo, setPhoto] = useState<File | null>(null)
  const [photoPreview, setPhotoPreview] = useState<string | null>(null)
  const [photoUrl, setPhotoUrl] = useState<string | null>(null)
  const [uploadingPhoto, setUploadingPhoto] = useState(false)

  const [expenseDate, setExpenseDate] = useState<string>(
    new Date().toISOString().split('T')[0]
  )
  const [receiptNumber, setReceiptNumber] = useState('')
  const [gallons, setGallons] = useState('')
  const [unitPrice, setUnitPrice] = useState('')
  const [notes, setNotes] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  // Resolver máquina + companyId del contexto
  const dealCtx = context?.kind === 'DEAL' ? context : null
  const opCtx = context?.kind === 'OPERATION' ? context : null
  const machineryId = dealCtx?.machineryId ?? null
  const subcontractedMachineryId = dealCtx?.subcontractedMachineryId ?? null
  const assetId = dealCtx?.transportAssetId ?? opCtx?.transportAssetId ?? null
  const companyId = dealCtx?.assetCompanyId ?? opCtx?.companyId ?? null
  const licensePlate = dealCtx?.licensePlate ?? opCtx?.licensePlate ?? null

  const canSubmit = useMemo(() => {
    return (
      !submitting &&
      !!photoUrl &&
      Number(gallons) > 0 &&
      Number(unitPrice) > 0 &&
      !!companyId &&
      (machineryId || subcontractedMachineryId)
    )
  }, [submitting, photoUrl, gallons, unitPrice, companyId, machineryId, subcontractedMachineryId])

  const totalAmount = useMemo(() => {
    const g = Number(gallons) || 0
    const p = Number(unitPrice) || 0
    return g * p
  }, [gallons, unitPrice])

  // Auto-pricing: último valor galón de la misma máquina
  useEffect(() => {
    if (!machineryId && !subcontractedMachineryId && !companyId) return
    if (unitPrice) return // ya escribió manual
    machineryExpensesService
      .getLastFuelUnitPrice({
        machineryId,
        subcontractedMachineryId,
        companyId,
      })
      .then((p) => {
        if (p) setUnitPrice(String(p))
      })
      .catch(() => {})
  }, [machineryId, subcontractedMachineryId, companyId])  // eslint-disable-line react-hooks/exhaustive-deps

  // Preview foto
  useEffect(() => {
    if (!photo) {
      setPhotoPreview(null)
      return
    }
    const url = URL.createObjectURL(photo)
    setPhotoPreview(url)
    return () => URL.revokeObjectURL(url)
  }, [photo])

  const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setPhoto(file)
    setUploadingPhoto(true)
    setError(null)
    try {
      const url = await machineryExpensesService.uploadPhoto(file)
      setPhotoUrl(url)
    } catch (e: any) {
      setError('No se pudo subir la foto. Intenta de nuevo.')
      setPhoto(null)
    } finally {
      setUploadingPhoto(false)
    }
  }

  const handleSubmit = async () => {
    if (!canSubmit) return
    setSubmitting(true)
    setError(null)
    try {
      await machineryExpensesService.create({
        machineryId: machineryId || undefined,
        subcontractedMachineryId: subcontractedMachineryId || undefined,
        assetId: assetId || undefined,
        companyId: companyId!,
        type: 'FUEL',
        expenseDate,
        receiptNumber: receiptNumber || undefined,
        quantity: Number(gallons),
        unitPrice: Number(unitPrice),
        totalAmount,
        photoUrl: photoUrl!,
        notes: notes || undefined,
      })
      setSuccess(true)
    } catch (e: any) {
      setError(e?.response?.data?.message || 'No se pudo registrar el combustible')
    } finally {
      setSubmitting(false)
    }
  }

  const handleAnother = () => {
    setPhoto(null)
    setPhotoUrl(null)
    setReceiptNumber('')
    setGallons('')
    setNotes('')
    setSuccess(false)
  }

  if (ctxLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loading size="lg" text="Cargando contexto…" />
      </div>
    )
  }

  if (!dealCtx && !opCtx) {
    return (
      <div className="min-h-screen bg-gray-100 p-4">
        <header className="bg-retri-blue px-3 py-3 flex items-center gap-2 -mx-4 -mt-4 mb-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="text-white">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-white font-semibold">Cargue de combustible</h1>
        </header>
        <Alert variant="error">No tienes un negocio activo para registrar combustible.</Alert>
      </div>
    )
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gray-100 p-4">
        <header className="bg-retri-blue px-3 py-3 flex items-center gap-2 -mx-4 -mt-4 mb-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} className="text-white">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-white font-semibold">Cargue registrado</h1>
        </header>
        <Card>
          <CardContent className="p-4 text-center space-y-3">
            <Fuel className="w-12 h-12 mx-auto text-retri-green" />
            <h2 className="text-lg font-semibold">¡Cargue registrado!</h2>
            <p className="text-sm text-gray-600">
              {formatGallons(gallons)} GL · ${totalAmount.toLocaleString('es-CO')}
            </p>
            <div className="flex gap-2 justify-center pt-2">
              <Button variant="outline" onClick={() => navigate('/dashboard')}>Volver</Button>
              <Button onClick={handleAnother}>Otro cargue</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <header className="bg-retri-blue px-3 py-3 flex items-center gap-2">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="text-white">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-white font-semibold">Cargue de combustible</h1>
      </header>

      <main className="max-w-xl mx-auto p-4 space-y-4">
        {error && <Alert variant="error">{error}</Alert>}

        <Card>
          <CardContent className="p-4 space-y-3">
            <div className="text-sm">
              <p className="text-gray-500">Vehículo</p>
              <p className="font-semibold">{licensePlate || '—'}</p>
            </div>

            <div>
              <label className="text-xs text-gray-600 uppercase tracking-wide">
                Foto del recibo *
              </label>
              {photoPreview ? (
                <div className="relative mt-2">
                  <img src={photoPreview} className="w-full max-h-64 object-contain rounded border" />
                  <button
                    type="button"
                    onClick={() => { setPhoto(null); setPhotoUrl(null) }}
                    className="absolute top-1 right-1 bg-white rounded-full p-1 shadow"
                  >
                    <X className="w-4 h-4" />
                  </button>
                  {uploadingPhoto && (
                    <div className="absolute inset-0 bg-black/30 flex items-center justify-center rounded">
                      <Loading size="md" text="Subiendo…" />
                    </div>
                  )}
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="mt-2 w-full border-2 border-dashed border-gray-300 rounded p-6 flex flex-col items-center text-gray-500 hover:bg-gray-50"
                >
                  <Camera className="w-8 h-8 mb-1" />
                  <span className="text-sm">Tomar foto del recibo</span>
                </button>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={handlePhotoChange}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Input
                label="Galones *"
                type="number"
                inputMode="decimal"
                value={gallons}
                onChange={(e) => setGallons(e.target.value)}
                placeholder="50"
              />
              <Input
                label="Valor galón *"
                type="number"
                inputMode="decimal"
                value={unitPrice}
                onChange={(e) => setUnitPrice(e.target.value)}
                placeholder="11050"
              />
            </div>

            <div className="bg-gray-50 rounded p-2 text-sm">
              <span className="text-gray-500">Total:</span>{' '}
              <span className="font-semibold">
                ${totalAmount.toLocaleString('es-CO', { maximumFractionDigits: 0 })}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Input
                label="Fecha"
                type="date"
                value={expenseDate}
                onChange={(e) => setExpenseDate(e.target.value)}
              />
              <Input
                label="N° recibo"
                value={receiptNumber}
                onChange={(e) => setReceiptNumber(e.target.value)}
                placeholder="39434"
              />
            </div>

            <Input
              label="Observación (opcional)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />

            <Button onClick={handleSubmit} disabled={!canSubmit} className="w-full">
              {submitting ? 'Registrando…' : 'Registrar combustible'}
            </Button>
            {!photoUrl && !uploadingPhoto && (
              <p className="text-xs text-center text-gray-500">
                La foto del recibo es obligatoria
              </p>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  )
}

export default RegisterDumperFuel
